import { ApiError } from "@/api/error";

export interface ErrorDescription {
    title: string;
    message: string;
    /** The server isn't answering: the page can wait for it to come back. */
    unreachable?: boolean;
}

const UNREACHABLE: ErrorDescription = {
    title: "Can't reach the server",
    message: "The surveys server isn't responding. It may be starting up or restarting.",
    unreachable: true,
};

/** What a full-page error says: never a bare code like "UNKNOWN". A
 *  failed fetch (TypeError), a gateway status, or a 5xx that isn't the
 *  server's own problem body means the server isn't answering; otherwise
 *  the server's detail wins, with the status as the fallback. The title is
 *  a reason phrase ("Bad Request") and never shown. */
export function describeError(error: unknown): ErrorDescription {
    if (error instanceof TypeError) return UNREACHABLE;
    if (!(error instanceof ApiError)) {
        return { title: "Something went wrong", message: error instanceof Error ? error.message : String(error) };
    }
    // The server always answers with a problem body, so a 5xx without one
    // came from a proxy in front of it: dev's Vite proxy or the ingress,
    // while the server is down or starting.
    const opaque = error.opaque;
    if (error.status === 502 || error.status === 503 || error.status === 504) return UNREACHABLE;
    if (opaque && error.status >= 500) return UNREACHABLE;
    // An opaque body (an HTML error page, a proxy's text) isn't worth showing.
    const detail = opaque ? undefined : error.detail;
    if (error.status === 404) {
        return { title: "Not found", message: detail ?? "This page or item doesn't exist, or was deleted." };
    }
    if (error.status === 403) {
        return { title: "No access", message: detail ?? "You don't have access to this." };
    }
    if (error.status >= 500) {
        return {
            title: "Something went wrong",
            message: detail ?? `The server had a problem (${error.status}). Try again in a moment.`,
        };
    }
    return {
        title: "Something went wrong",
        message: detail ?? `The request failed (${error.status}).`,
    };
}
